// Dimension resolution shared by the asset card and the naming step's
// dimension group headers. Both have to read a creative's size the same way
// or a card would sit under a header that disagrees with the size printed on
// the card itself.
//
// The creative shapes each store size somewhere different (see CLAUDE.md):
// uploaded files get `dimensions` from the image/video probe, existing GCM
// picks carry `dcmCreative`, and anything sized by hand only has
// `taxonomy.dimensions`.
//
// Video and audio are the awkward case. CM360 traffics them at 0x0 by design,
// so there is no pixel size to group on — not in `creative.size` and not in
// `creativeAssets[].size`, which the API documents as applicable to image and
// rich-media types only. Their real grouping key is duration, which CM360
// detects on ingest and exposes read-only as `mediaDuration`.

export const NO_DIMENSIONS = "N/A";

const toPositiveSeconds = (value) => {
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Detected duration in seconds for an existing GCM video/audio creative.
 * @param {object} asset - a creative from state.creatives.byId
 * @returns {number|null} seconds, or null if this isn't a timed creative
 */
export function getMediaDurationSeconds(asset) {
  const dcm = asset && asset.dcmCreative;
  if (!dcm) return null;

  // Top-level mediaDuration is CM360's own detection and covers
  // INSTREAM_VIDEO, INSTREAM_AUDIO, all RICH_MEDIA and all VPAID. Prefer it.
  const topLevel = toPositiveSeconds(dcm.mediaDuration);
  if (topLevel !== null) return topLevel;

  // Per-asset duration is the fallback for responses whose field mask drops
  // the top-level value — getCreativesByIds in BartAPI asks for
  // `creativeAssets` wholesale but not `mediaDuration`, so ChangeLog and the
  // assigned-creatives list land here.
  const assets = dcm.creativeAssets || [];
  for (const a of assets) {
    const assetLevel = toPositiveSeconds(a && a.mediaDuration);
    if (assetLevel !== null) return assetLevel;
  }

  return null;
}

/**
 * The `:NN` entries configured in the Dimensions type group.
 * @param {object} typeGroups - state.typeGroups
 * @returns {string[]}
 */
export function getDurationOptions(typeGroups) {
  const types =
    (typeGroups &&
      typeGroups.byGroupName &&
      typeGroups.byGroupName.Dimensions &&
      typeGroups.byGroupName.Dimensions.types) ||
    [];

  return types
    .map((t) => t && t.value)
    .filter((v) => typeof v === "string" && /^:\d+$/.test(v));
}

/**
 * Snap a real duration to the nearest configured `:NN`.
 *
 * Nearest, not largest-at-or-below: encoders routinely land a :30 spot at
 * 29.4s, and snapping down files it under :15 — a whole spot length off. Ties
 * go to the longer value, matching how the spots are sold.
 *
 * @param {number} seconds
 * @param {string[]} durationOptions - from getDurationOptions
 * @returns {string|null} e.g. ":30", or null if nothing is configured
 */
export function snapSecondsToDuration(seconds, durationOptions) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  if (!durationOptions || durationOptions.length === 0) return null;

  let best = null;
  let bestValue = -Infinity;
  let bestDelta = Infinity;

  for (const option of durationOptions) {
    const n = parseInt(option.slice(1), 10);
    if (!Number.isFinite(n)) continue;

    const delta = Math.abs(n - seconds);
    if (best === null || delta < bestDelta || (delta === bestDelta && n > bestValue)) {
      best = option;
      bestValue = n;
      bestDelta = delta;
    }
  }

  return best;
}

/**
 * The dimension string to show for an asset, e.g. "300 x 250", ":30", "N/A".
 * @param {object} asset - a creative from state.creatives.byId
 * @param {string[]} [durationOptions] - from getDurationOptions; without it a
 *   detected duration falls back to its raw rounded `:NN`
 * @returns {string}
 */
export function getDisplayDimensions(asset, durationOptions) {
  if (!asset) return NO_DIMENSIONS;

  if (asset.dcmCreative !== undefined) {
    const dcm = asset.dcmCreative;

    // Tracking creatives have no size in GCM but always traffic as 1x1.
    if (dcm.type !== undefined && dcm.type.includes("TRACKING")) {
      return "1 x 1";
    }

    // Check duration before size: a video's size is 0x0, so reading size
    // first drops every video into a single meaningless bucket.
    const seconds = getMediaDurationSeconds(asset);
    if (seconds !== null) {
      return (
        snapSecondsToDuration(seconds, durationOptions) ||
        ":" + Math.round(seconds)
      );
    }

    if (dcm.size) {
      return `${dcm.size.width} x ${dcm.size.height}`;
    }
    return NO_DIMENSIONS;
  }

  if (asset.dimensions && asset.dimensions !== NO_DIMENSIONS) {
    return asset.dimensions;
  }

  // taxonomy.dimensions is stored without spaces ("300x250") and may be a
  // duration for media creatives (":30"), which has no "x" to split on.
  if (asset.taxonomy && asset.taxonomy.dimensions) {
    return asset.taxonomy.dimensions.split("x").join(" x ");
  }

  return NO_DIMENSIONS;
}

/**
 * Heading text for a dimension group. Bare "N/A" reads as a broken header, so
 * the unsized group gets spelled out. A 0x0 group is a video or audio whose
 * duration CM360 never detected — say that rather than showing "0 x 0".
 * @param {string} key - a value returned by getDisplayDimensions
 * @returns {string}
 */
export function getDimensionGroupLabel(key) {
  if (key === NO_DIMENSIONS) return "Dimensions not set";
  if (key === "0 x 0") return "Duration not detected";
  return key;
}

/**
 * Partition ids into groups of equal dimension.
 *
 * Groups come back in first-appearance order rather than sorted by size, so
 * the list still reflects the order the creatives were uploaded or dragged
 * into — sorting would silently rearrange a sequence the user set.
 *
 * @param {string[]} ids  - ids to group, already filtered to what's rendered
 * @param {object} byId   - state.creatives.byId
 * @param {string[]} [durationOptions] - from getDurationOptions
 * @returns {Array<{ key: string, label: string, ids: string[] }>}
 */
export function groupIdsByDimension(ids, byId, durationOptions) {
  const groups = [];
  const byKey = new Map();

  for (const id of ids) {
    const key = getDisplayDimensions(byId[id], durationOptions);
    let group = byKey.get(key);
    if (!group) {
      group = { key, label: getDimensionGroupLabel(key), ids: [] };
      byKey.set(key, group);
      groups.push(group);
    }
    group.ids.push(id);
  }

  return groups;
}
